import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from 'fpay-ui';
import parse from 'html-react-parser';

import { useConnector } from '@hooks';
import { useTranslation } from 'react-i18next';

const Background = styled.div`
  background: #ffffff;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  padding: 24px;
  align-items: center;

  > button:last-of-type {
    margin-left: 16px;
  }
  > button:first-of-type {
    margin-left: auto;
  }

  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    padding: 16px 24px;
  }
`;

const Image = styled.img`
  max-width: 62px;
  max-height: 62px;
  margin-right: 24px;
  object-fit: scale-down;
`;

const Title = styled.div`
  display: flex;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.gray800};

  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    display: none;
  }
`;

const Body = styled.div`
  padding: 3px 110px 40px 110px;
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    padding: 0px 24px 24px 24px;
  }
  font-style: normal;
  font-family: Roboto;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0em;
  text-align: left;
  color: ${({ theme }) => theme.colors.gray700};

  > ul {
    padding-left: 24px !important;
  }

  > p:first-of-type {
    margin-top: 0px;
  }
`;

const DownloadButton = styled(Button)`
  width: 150px;
  height: 48px;
  white-space: nowrap;

  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.01em;
  text-align: center;
  &:hover {
    ${({ disabled }) => disabled && 'filter: unset; cursor: unset;'}
  }
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    display: none;
  }
`;

const DownloadGuideLink = styled(DownloadButton)`
  width: 185px;
  padding: 12px 0px;
  ${({ showCommingSoon }) => showCommingSoon && 'margin-top: 8px;'}
`;

const ReturnButton = styled(Button)`
  padding: 0px 0px 48px 134px;
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    display: none;
  }
`;

const Resume = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.gray600};

  display: none;
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    display: unset;
  }
`;

const MobileDownloadButton = styled(DownloadButton)`
  display: none;
  width: auto;
  margin: 0px 24px 8px 24px;
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    display: unset;
  }
`;

const MobileDownloadGuideLink = styled(DownloadGuideLink)`
  display: none;
  width: auto;
  margin: 0px 24px 32px 24px;
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    display: unset;
  }
`;

const ConnectorDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { download } = useConnector();
  const {
    description,
    downloadButtonConfig,
    downloadGuideConfig,
    imageSrc,
    resume,
    shouldShowDownloadButton,
    showCommingSoon,
    title,
  } = history?.location?.state ?? {};

  const handleDownload = useCallback(
    (event) => {
      event.stopPropagation();
      download(downloadButtonConfig.link, `${title}-plugin`);
    },
    [download, downloadButtonConfig, title]
  );
  const handleLinkDownload = useCallback(
    (event) => {
      event.stopPropagation();
      download(downloadGuideConfig.link, `${title}-guide`);
    },
    [download, downloadGuideConfig, title]
  );

  const handleReturn = useCallback(() => history.goBack(), [history]);

  return (
    <Background>
      <Header>
        <Image src={imageSrc} alt="#" />
        <Resume>{resume}</Resume>
        <Title>{title}</Title>
        <DownloadGuideLink
          onClick={handleLinkDownload}
          disabled={!downloadGuideConfig?.active}
          type="outlined"
        >
          {t('configuration.screens.connectors.downloadGuideButton')}
        </DownloadGuideLink>
        {shouldShowDownloadButton && !showCommingSoon && (
          <DownloadButton onClick={handleDownload} disabled={!downloadButtonConfig?.active}>
            {t('configuration.screens.connectors.downloadButton')}
          </DownloadButton>
        )}
      </Header>
      <Body>{parse(description)}</Body>
      <ReturnButton type="link" onClick={handleReturn}>
        {t('common.back')}
      </ReturnButton>
      {shouldShowDownloadButton && !showCommingSoon && (
        <MobileDownloadButton onClick={handleDownload} disabled={!downloadButtonConfig?.active}>
          {t('configuration.screens.connectors.downloadButton')}
        </MobileDownloadButton>
      )}
      <MobileDownloadGuideLink
        onClick={handleLinkDownload}
        disabled={!downloadGuideConfig?.active}
        type="outlined"
      >
        {t('configuration.screens.connectors.downloadGuideButton')}
      </MobileDownloadGuideLink>
    </Background>
  );
};

export default ConnectorDetail;
